import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import Header from "@/components/Header";
import ProgressGauge from "@/components/ProgressGauge";
import KPICards from "@/components/KPICards";
import MonthlyChart from "@/components/MonthlyChart";
import DrivingTrendCard from "@/components/DrivingTrendCard";
import TripTable from "@/components/TripTable";
import EnergyTable from "@/components/EnergyTable";
import AddTripDialog from "@/components/dialogs/AddTripDialog";
import BulkImportDialog from "@/components/dialogs/BulkImportDialog";
import VehicleManageDialog from "@/components/dialogs/VehicleManageDialog";
import AddEnergyDialog from "@/components/dialogs/AddEnergyDialog";
import AddDataDialog from "@/components/dialogs/AddDataDialog";
import SettingsDialog from "@/components/dialogs/SettingsDialog";
import FeedbackDialog from "@/components/dialogs/FeedbackDialog";
import WhatsNewDialog from "@/components/dialogs/WhatsNewDialog";
import Footer from "@/components/Footer";
import { VehiclesAPI, TripsAPI, StatsAPI, EnergyAPI, SettingsAPI, MaintenanceAPI } from "@/lib/api";
import MaintenanceStatusCard from "@/components/MaintenanceStatusCard";
import { detectCurrency, symbolFor } from "@/lib/currency";
import useConfirm from "@/lib/useConfirm";
import { useNavigate } from "react-router-dom";
import { Plus, Car as CarIcon } from "lucide-react";
import {
  computeWeeklyTrends,
  projectYearEnd,
  buildMonthlyForecast,
  buildInsights,
  getPolicyYearWindow,
  computeAnnualPaceStatus,
  computeSafeDailyAverage,
} from "@/lib/insights";
import SmartSummaryBanner from "@/components/SmartSummaryBanner";
import VehicleOverviewCard from "@/components/VehicleOverviewCard";
import InsightsPanel from "@/components/InsightsPanel";
import MobileFab from "@/components/MobileFab";
import { Button } from "@/components/ui/button";
import { Route as RouteIcon, Zap as ZapIcon } from "lucide-react";
import logger from "@/lib/logger";
import { resolveTz } from "@/lib/tz";

export default function Dashboard() {
  const [vehicles, setVehicles] = useState([]);
  const [trips, setTrips] = useState([]);
  const [energyEvents, setEnergyEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [perVehicleStats, setPerVehicleStats] = useState({}); // {vehicleId: stats}
  const [settings, setSettings] = useState(null);
  const [unit, setUnit] = useState(() => localStorage.getItem("mileage_unit") || "km");
  const [selectedVehicleId, setSelectedVehicleId] = useState("all");
  const [usageType, setUsageType] = useState("all"); // "all" | "personal" | "business"
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [loading, setLoading] = useState(true);

  const [showAddData, setShowAddData] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkImportKind, setBulkImportKind] = useState(null);
  const [bulkImportVehicleId, setBulkImportVehicleId] = useState(null);
  const [showManage, setShowManage] = useState(false);
  const [showEnergy, setShowEnergy] = useState(false);
  const [energyDefaults, setEnergyDefaults] = useState({ vehicleId: null });
  const [editingEnergy, setEditingEnergy] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // In-app confirm dialog — replaces `window.confirm` which is blocked
  // inside Emergent's preview iframe on modern Chromium.
  const { confirm, ConfirmDialog } = useConfirm();
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem("mileage_unit", unit);
  }, [unit]);

  const refresh = useCallback(async () => {
    const params = {};
    if (selectedVehicleId && selectedVehicleId !== "all") params.vehicle_id = selectedVehicleId;
    if (usageType && usageType !== "all") params.usage_type = usageType;
    if (dateRange?.from) params.start_date = dateRange.from.toISOString().slice(0, 10);
    if (dateRange?.to) params.end_date = dateRange.to.toISOString().slice(0, 10);
    try {
      const [t, s, e] = await Promise.all([
        TripsAPI.list(params),
        StatsAPI.get(params),
        EnergyAPI.list(params),
      ]);
      setTrips(t);
      setStats(s);
      setEnergyEvents(e);

      // Per-vehicle stats — only needed in "All Vehicles" mode AND when the
      // user actually has multiple active vehicles. Used by the morphed
      // widgets (ProgressGauge bars, per-vehicle efficiency, spend split).
      // Vehicle filter is overridden per request; usage + date filters are
      // preserved so the comparison stays apples-to-apples.
      const active = vehicles.filter((v) => !v.archived);
      const isAllMode = !selectedVehicleId || selectedVehicleId === "all";
      if (isAllMode && active.length > 1) {
        const perStatsParams = { ...params };
        delete perStatsParams.vehicle_id;
        const entries = await Promise.all(
          active.map(async (v) => {
            try {
              const st = await StatsAPI.get({ ...perStatsParams, vehicle_id: v.id });
              return [v.id, st];
            } catch (err) {
              logger.warn("dashboard-per-vehicle-stats-failed", err?.message || String(err));
              return [v.id, null];
            }
          }),
        );
        setPerVehicleStats(Object.fromEntries(entries));
      } else {
        setPerVehicleStats({});
      }
    } catch (e) {
      logger.error("dashboard-refresh-failed", e?.message || String(e));
      toast.error("Failed to load data");
    }
  }, [selectedVehicleId, usageType, dateRange, vehicles]);

  // Initial load with seeding + settings + currency reconciliation
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const vs = await VehiclesAPI.list();
        setVehicles(vs);
        // Settings: read; if first-run, reconcile with detected currency
        let s = await SettingsAPI.get();
        const stored = localStorage.getItem("mileage_currency_reconciled");
        if (!stored) {
          const det = detectCurrency();
          if (det.currency && det.currency !== s.currency) {
            s = await SettingsAPI.update({ currency: det.currency, currency_symbol: det.symbol });
          }
          localStorage.setItem("mileage_currency_reconciled", "1");
        }
        setSettings(s);
      } catch (e) {
        logger.error("dashboard-initial-load-failed", e?.message || String(e));
        toast.error("Failed to load vehicles");
      } finally {
        setLoading(false);
      }
    })();
    // Initial-load effect — intentionally runs once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Maintenance summary — either scoped to the selected vehicle or aggregate.
  const [maintSummary, setMaintSummary] = useState(null);
  useEffect(() => {
    const params = selectedVehicleId !== "all" ? { vehicle_id: selectedVehicleId } : undefined;
    MaintenanceAPI.summary(params)
      .then(setMaintSummary)
      .catch(() => setMaintSummary(null));
  }, [selectedVehicleId, vehicles.length]);

  const selectedVehicle = useMemo(
    () => vehicles.find((v) => v.id === selectedVehicleId),
    [vehicles, selectedVehicleId]
  );

  const activeVehicles = useMemo(
    () => vehicles.filter((v) => !v.archived),
    [vehicles]
  );

  // Calculate aggregate yearly used + limit
  const { aggregateUsedKm, aggregateLimitKm, gaugeName } = useMemo(() => {
    if (selectedVehicle) {
      return {
        aggregateUsedKm: stats?.year_used_km || 0,
        aggregateLimitKm: selectedVehicle.yearly_limit_km || 0,
        gaugeName: selectedVehicle.name,
      };
    }
    const totalLimit = activeVehicles.reduce((acc, v) => acc + (v.yearly_limit_km || 0), 0);
    return {
      aggregateUsedKm: stats?.year_used_km || 0,
      aggregateLimitKm: totalLimit,
      gaugeName: "All Active",
    };
  }, [selectedVehicle, activeVehicles, stats]);

  // Effective policy start date for pace/projection math.
  // - Single vehicle selected → that vehicle's policy date.
  // - "All" with exactly 1 active vehicle → that vehicle's policy date (so the
  //   "All" view matches the single-vehicle view when only one car is active).
  // - "All" with multiple actives sharing the same policy_start_date → use it.
  // - Otherwise → undefined (falls back to calendar year).
  const effectivePolicyStartDate = useMemo(() => {
    if (selectedVehicle) return selectedVehicle.policy_start_date || undefined;
    const dates = activeVehicles
      .map((v) => v.policy_start_date)
      .filter((d) => !!d);
    if (dates.length === 0) return undefined;
    if (dates.length !== activeVehicles.length) return undefined;
    const first = dates[0];
    return dates.every((d) => d === first) ? first : undefined;
  }, [selectedVehicle, activeVehicles]);

  // Predictive insights (computed entirely on the frontend)
  const trends = useMemo(() => computeWeeklyTrends(trips), [trips]);
  const projection = useMemo(() => {
    const yearUsed = stats?.year_used_km || 0;
    if (yearUsed <= 0 || aggregateLimitKm <= 0) return null;
    return projectYearEnd({
      yearUsedKm: yearUsed,
      yearlyLimitKm: aggregateLimitKm,
      today: new Date(),
      policyStartDate: effectivePolicyStartDate,
    });
  }, [stats, aggregateLimitKm, effectivePolicyStartDate]);

  // Smart summary derived metrics — pace messaging is driven EXCLUSIVELY by
  // projected year-end vs annual allowance (single source of truth). Monthly
  // metrics on this banner are informational only.
  const smartSummary = useMemo(() => {
    if (!stats || !projection) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Policy-year window math — primary source of truth for pacing.
    const { start, end } = getPolicyYearWindow(today, effectivePolicyStartDate);
    const daysInPolicyYear = Math.max(1, Math.round((end - start) / 86400000));
    const daysElapsedInPolicyYear = Math.max(
      0,
      Math.min(daysInPolicyYear, Math.round((today - start) / 86400000))
    );
    const daysRemainingInPolicyYear = Math.max(
      1,
      daysInPolicyYear - daysElapsedInPolicyYear
    );

    const annualLimit = aggregateLimitKm || 0;
    const annualUsed = stats.year_used_km || 0;
    const remainingAnnualKm = Math.max(0, annualLimit - annualUsed);

    const annualPace = computeAnnualPaceStatus({
      projectedKm: projection.km,
      annualLimitKm: annualLimit,
    });

    const safeDailyAverage = computeSafeDailyAverage({
      remainingAnnualKm,
      remainingPolicyDays: daysRemainingInPolicyYear,
    });

    return {
      annualPace,
      safeDailyAverage,
      daysRemainingInPolicyYear,
      monthLabel: stats.month_label,
    };
  }, [stats, projection, aggregateLimitKm, effectivePolicyStartDate]);

  // Monthly forecast (actual + projected ghost bars)
  const monthlyForecast = useMemo(() => {
    if (!stats?.monthly) return [];
    const dailyRate = projection?.dailyRate || 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const { start, end } = getPolicyYearWindow(today, effectivePolicyStartDate);
    const daysInPolicyYear = Math.round((end - start) / 86400000);
    return buildMonthlyForecast({
      monthly: stats.monthly,
      today,
      daysInPolicyYear,
      dailyRate,
    });
  }, [stats, projection, effectivePolicyStartDate]);

  // Auto-generated insights
  const insights = useMemo(() => {
    return buildInsights({
      trips,
      yearUsedKm: stats?.year_used_km || 0,
      yearlyLimitKm: aggregateLimitKm,
      today: new Date(),
      policyStartDate: effectivePolicyStartDate,
    });
  }, [trips, stats, aggregateLimitKm, effectivePolicyStartDate]);

  // Per-vehicle insights — only computed in "All Vehicles" mode with >1 active
  // car. Each insight is tagged with the vehicle that triggered it so the
  // InsightsPanel can render a vehicle badge alongside the copy.
  const isMultiAll = useMemo(
    () =>
      (selectedVehicleId === "all" || !selectedVehicleId) && activeVehicles.length > 1,
    [selectedVehicleId, activeVehicles],
  );

  // `morphCapable` is true whenever the household has >1 active vehicle —
  // regardless of the current dropdown filter. We use this to pin min-heights
  // on the morphing widgets so the multi↔single toggle never changes the
  // outer card dimensions (strict Visual Stability requirement).
  const morphCapable = activeVehicles.length > 1;

  // Per-vehicle monthly forecast — used to render the stacked Monthly Mileage
  // chart in multi-vehicle mode. Map of vehicle.id → 12-month forecast array.
  const perVehicleForecast = useMemo(() => {
    if (!isMultiAll) return {};
    const out = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (const v of activeVehicles) {
      const vStats = perVehicleStats[v.id];
      if (!vStats?.monthly) continue;
      const { start, end } = getPolicyYearWindow(today, v.policy_start_date || undefined);
      const daysInPolicyYear = Math.round((end - start) / 86400000);
      const yearUsed = vStats.year_used_km || 0;
      const daysElapsed = Math.max(
        1,
        Math.min(daysInPolicyYear, Math.round((today - start) / 86400000)),
      );
      const dailyRate = yearUsed / daysElapsed;
      out[v.id] = buildMonthlyForecast({
        monthly: vStats.monthly,
        today,
        daysInPolicyYear,
        dailyRate,
      });
    }
    return out;
  }, [isMultiAll, activeVehicles, perVehicleStats]);

  const perVehicleInsights = useMemo(() => {
    if (!isMultiAll) return [];
    const out = [];
    for (const v of activeVehicles) {
      const vTrips = trips.filter((t) => t.vehicle_id === v.id);
      const vStats = perVehicleStats[v.id];
      if (!vStats) continue;
      const vInsights = buildInsights({
        trips: vTrips,
        yearUsedKm: vStats.year_used_km || 0,
        yearlyLimitKm: v.yearly_limit_km || 0,
        today: new Date(),
        policyStartDate: v.policy_start_date || undefined,
      });
      vInsights.forEach((it) =>
        out.push({ ...it, vehicleId: v.id, vehicleName: v.name }),
      );
    }
    return out;
  }, [isMultiAll, activeVehicles, trips, perVehicleStats]);

  // Handlers
  const handleAddTrip = async (data) => {
    try {
      if (editingTrip) {
        // EDIT — strip vehicle_id (immutable), send only what changed
        const { vehicle_id, ...payload } = data;
        await TripsAPI.update(editingTrip.id, payload);
        toast.success("Trip updated");
      } else {
        await TripsAPI.create(data);
        toast.success("Trip logged");
      }
      setEditingTrip(null);
      refresh();
    } catch (e) {
      toast.error(editingTrip ? "Failed to update trip" : "Failed to add trip");
      throw e;
    }
  };

  const handleDeleteTrip = async (id) => {
    const ok = await confirm({
      title: "Delete trip?",
      description:
        "This permanently removes the trip from your history. This can't be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await TripsAPI.remove(id);
      toast.success("Trip removed");
      refresh();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleEditTrip = (t) => {
    setEditingTrip(t);
    setShowAdd(true);
  };

  const handleVehicleCreate = async (data) => {
    try {
      const v = await VehiclesAPI.create(data);
      setVehicles((prev) => [...prev, v]);
      toast.success("Vehicle added");
    } catch {
      toast.error("Failed to add vehicle");
    }
  };

  const handleVehicleUpdate = async (id, data) => {
    try {
      const v = await VehiclesAPI.update(id, data);
      setVehicles((prev) => prev.map((x) => (x.id === id ? v : x)));
      toast.success("Vehicle updated");
      refresh();
    } catch {
      toast.error("Update failed");
    }
  };

  const handleVehicleDelete = async (id) => {
    // `window.confirm()` is blocked inside Emergent's preview iframe on
    // modern Chromium — routed through the shared in-app confirm dialog.
    const ok = await confirm({
      title: "Delete vehicle?",
      description:
        "This permanently removes the vehicle and all of its logged trips and energy events. This can't be undone.",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      destructive: true,
    });
    if (!ok) return;
    try {
      await VehiclesAPI.remove(id);
      setVehicles((prev) => prev.filter((x) => x.id !== id));
      if (selectedVehicleId === id) setSelectedVehicleId("all");
      toast.success("Vehicle deleted");
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleAddEnergy = async (data) => {
    try {
      if (editingEnergy) {
        // EDIT — strip immutable fields, send only mutable subset
        const { vehicle_id, type, ...payload } = data;
        await EnergyAPI.update(editingEnergy.id, payload);
        toast.success("Energy entry updated");
      } else {
        await EnergyAPI.create(data);
        toast.success("Energy entry saved");
      }
      setEditingEnergy(null);
      refresh();
    } catch (e) {
      toast.error(editingEnergy ? "Failed to update energy entry" : "Failed to save energy entry");
      throw e;
    }
  };

  const handleDeleteEnergy = async (id) => {
    const ok = await confirm({
      title: "Delete energy entry?",
      description:
        "This permanently removes the charging session or fuel fill-up from your history. This can't be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await EnergyAPI.remove(id);
      toast.success("Entry removed");
      refresh();
    } catch (e) {
      toast.error("Failed to delete entry");
    }
  };

  const handleEditEnergy = (e) => {
    setEditingEnergy(e);
    setEnergyDefaults({ vehicleId: e.vehicle_id, type: e.type });
    setShowEnergy(true);
  };

  // Single router from AddDataDialog → the right downstream dialog.
  const handleAddDataPick = ({ mode, kind, vehicleId }) => {
    if (mode === "manual") {
      if (kind === "trip") {
        setShowAdd(true);
      } else if (kind === "charging" || kind === "fuel") {
        setEnergyDefaults({ vehicleId, type: kind === "charging" ? "charge" : "fuel" });
        setShowEnergy(true);
      }
    } else if (mode === "bulk") {
      setBulkImportKind(kind);
      setBulkImportVehicleId(vehicleId || null);
      setShowBulkImport(true);
    }
  };

  const handleBulkImported = ({ kind, inserted, errors }) => {
    const label = kind === "trip" ? "trips" : kind === "charging" ? "charging entries" : "fuel entries";
    toast.success(
      `Imported ${inserted} ${label}${errors?.length ? ` (${errors.length} skipped)` : ""}`,
    );
    refresh();
  };

  const handleSaveSettings = async (data) => {
    try {
      const s = await SettingsAPI.update(data);
      setSettings(s);
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save settings");
    }
  };

  const currencySymbol = settings?.currency_symbol || symbolFor(settings?.currency || "USD");
  // Resolve the active IANA timezone: user setting → browser detection → UTC.
  // Used for default trip/energy dates and to stamp entry_timezone on save.
  const tz = useMemo(() => resolveTz(settings), [settings]);

  // Deductible energy tile — folded into the Smart Summary banner when the
  // active range actually has business trips. Backend `energy.charge_cost`
  // already includes any Monthly subscription cost (iter 43), so we don't
  // re-add it here.
  const deductibleTile = useMemo(() => {
    const businessKm = Number(stats?.business_km || 0);
    const personalKm = Number(stats?.personal_km || 0);
    const totalUsageKm = businessKm + personalKm;
    const chargeCost = Number(stats?.energy?.charge_cost || 0);
    const chargeKwh = Number(stats?.energy?.charge_kwh || 0);
    if (businessKm <= 0 || totalUsageKm <= 0 || chargeCost <= 0) return null;
    const ratio = businessKm / totalUsageKm;
    return {
      value: chargeCost * ratio,
      businessPct: ratio * 100,
      chargeCost,
      chargeKwh,
      currencySymbol,
    };
  }, [stats, currencySymbol]);

  return (
    <div className="min-h-screen bg-[var(--bg-deep)] text-[var(--text-primary)]">
      <Header
        vehicles={activeVehicles}
        selectedVehicleId={selectedVehicleId}
        onVehicleChange={setSelectedVehicleId}
        usageType={usageType}
        onUsageChange={setUsageType}
        dateRange={dateRange}
        onDateRangeChange={(r) => setDateRange(r || { from: undefined, to: undefined })}
        onAddData={() => setShowAddData(true)}
        onOpenSettings={() => setShowSettings(true)}
        onOpenFeedback={() => setShowFeedback(true)}
      />

      <main className="px-5 sm:px-6 lg:px-10 py-6 lg:py-8 pb-24 max-w-[1600px] mx-auto" data-testid="dashboard-main">
        {/* Hero title */}
        <div className="mb-5 lg:mb-6 fade-up">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-1 h-3 bg-[#E82127]" />
            <span className="label-tiny">Dashboard</span>
            {selectedVehicle && (
              <>
                <span className="text-[10px] tracking-wider text-[var(--text-faint)]">·</span>
                <span className="label-tiny text-[var(--text-primary)]">{selectedVehicle.name}</span>
              </>
            )}
          </div>
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-light track1.3ing-tighter">
            Vehicle <span className="text-[var(--text-muted)]">Analytics</span>
          </h1>
          {/* <p className="text-sm text-[var(--text-secondary)] mt-2">
            Track across {activeVehicles.length} active{" "}
            {activeVehicles.length === 1 ? "vehicle" : "vehicles"}.
          </p> */}
          <p className="text-sm text-[var(--text-secondary)] mt-2">
            Your {activeVehicles.length === 1 ? "vehicle" : "vehicles"} at a glance. Make smarter decisions with your data. Tracking across {activeVehicles.length} active{" "}
            {activeVehicles.length === 1 ? "vehicle" : "vehicles"}.
          </p>
        </div>

        {/* Empty-garage guard — analytics are meaningless without at
            least one vehicle. Mirrors the pattern on /vehicles so users
            hit the same "add your first car" CTA everywhere in the app. */}
        {activeVehicles.length === 0 ? (
          <div
            className="bg-[var(--bg-canvas)] border border-dashed border-[var(--border-hover)] p-12 lg:p-16 flex flex-col items-center justify-center text-center fade-up"
            style={{ borderRadius: 4 }}
            data-testid="dashboard-empty-garage"
          >
            <div
              className="w-12 h-12 mb-4 flex items-center justify-center bg-[var(--bg-elevated)] border border-[var(--border-subtle)]"
              style={{ borderRadius: 4 }}
            >
              <CarIcon
                className="w-5 h-5 text-[#E82127]"
                strokeWidth={1.75}
              />
            </div>
            <div className="label-tiny mb-2">Empty Garage</div>
            <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight text-[var(--text-primary)] mb-2">
              No vehicles yet.
            </h2>
            <p className="text-[var(--text-muted)] text-sm mb-6 max-w-[420px] leading-relaxed">
              Add your first vehicle to unlock mileage tracking, energy analytics,
              and cost insights across your garage.
            </p>
            <Button
              onClick={() => navigate("/vehicles")}
              className="bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)]"
              style={{ borderRadius: 2 }}
              data-testid="dashboard-empty-add-vehicle-btn"
            >
              <Plus className="w-4 h-4 mr-2" strokeWidth={2.25} />
              Add a Vehicle
            </Button>
          </div>
        ) : (
        <>
        {/* Top hero row: Vehicle Overview + Smart Summary side-by-side on lg+, stacked on mobile.
            Both children use h-full so the cards stretch to the row's tallest content. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3 items-stretch">
          <div className="fade-up h-full" style={{ animationDelay: "0.03s" }}>
            <VehicleOverviewCard
              vehicles={vehicles}
              selectedVehicleId={selectedVehicleId}
              trips={trips}
              energyEvents={energyEvents}
              stats={stats}
              projection={projection}
              unit={unit}
              currencySymbol={currencySymbol}
            />
          </div>

          <div className="fade-up h-full" style={{ animationDelay: "0.05s" }}>
            <SmartSummaryBanner
              projection={projection}
              annualPace={smartSummary?.annualPace}
              safeDailyAverage={smartSummary?.safeDailyAverage}
              daysRemainingInPolicyYear={smartSummary?.daysRemainingInPolicyYear}
              yearlyLimitKm={aggregateLimitKm}
              unit={unit}
              deductible={deductibleTile}
            />
          </div>
        </div>

        {/* Top row: gauge + (KPIs stacked above Monthly Pace) */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-3 fade-up" style={{ animationDelay: "0.1s" }}>
          <div className="lg:col-span-1">
            <ProgressGauge
              usedKm={aggregateUsedKm}
              limitKm={aggregateLimitKm}
              unit={unit}
              vehicleName={gaugeName}
              year={stats?.year || new Date().getFullYear()}
              projection={projection}
              isMultiAll={isMultiAll}
              morphCapable={morphCapable}
              vehicles={activeVehicles}
              perVehicleStats={perVehicleStats}
            />
          </div>
          <div className="lg:col-span-3 flex flex-col gap-3">
            <KPICards
              stats={stats}
              unit={unit}
              trends={trends}
              currencySymbol={currencySymbol}
              vehicles={activeVehicles}
              selectedVehicleId={selectedVehicleId}
              isMultiAll={isMultiAll}
              morphCapable={morphCapable}
              perVehicleStats={perVehicleStats}
            />
            {/* The Monthly Pace card has been retired — MileageIQ is a forecasting
                + guidance tool, not a quota tracker. The Driving Trend card
                compares current-month behavior to historical patterns and
                translates that into year-end impact. */}
            <DrivingTrendCard
              stats={stats}
              projection={projection}
              yearlyLimitKm={aggregateLimitKm}
              unit={unit}
              isMultiAll={isMultiAll}
              morphCapable={morphCapable}
              vehicles={activeVehicles}
              perVehicleStats={perVehicleStats}
            />
          </div>
        </div>

        {/* Monthly chart + Insights */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3 fade-up" style={{ animationDelay: "0.2s" }}>
          <div className="lg:col-span-2">
            <MonthlyChart
              forecast={monthlyForecast}
              unit={unit}
              yearUsedKm={stats?.year_used_km || 0}
              yearlyLimitKm={aggregateLimitKm}
              isMultiAll={isMultiAll}
              morphCapable={morphCapable}
              vehicles={activeVehicles}
              perVehicleForecast={perVehicleForecast}
            />
          </div>
          <div className="lg:col-span-1">
            <InsightsPanel
              insights={insights}
              stats={stats}
              unit={unit}
              currencySymbol={currencySymbol}
              deductibleRate={settings?.deductible_rate_per_km}
              isMultiAll={isMultiAll}
              morphCapable={morphCapable}
              perVehicleInsights={perVehicleInsights}
              vehicles={activeVehicles}
            />
          </div>
        </div>

        {/* Trip History */}
        <div className="fade-up" style={{ animationDelay: "0.25s" }}>
          <TripTable
            trips={trips}
            vehicles={activeVehicles}
            allVehicles={vehicles}
            unit={unit}
            onDelete={handleDeleteTrip}
            onEdit={handleEditTrip}
          />
        </div>

        {/* ✓ VISUAL SEPARATION BAR 
          We add an explicit vertical space to separate the tables cleanly.
        */}
        <div className="fade-up mt-6" style={{ animationDelay: "0.28s" }}>

        </div>

        {/* Energy History */}
        <div className="fade-up" style={{ animationDelay: "0.3s" }}>
          <EnergyTable
            events={energyEvents}
            vehicles={activeVehicles}
            allVehicles={vehicles}
            currencySymbol={currencySymbol}
            onDelete={handleDeleteEnergy}
            onEdit={handleEditEnergy}
          />
        </div>

        <Footer />
        </>
        )}
      </main>

      <AddDataDialog
        open={showAddData}
        onOpenChange={setShowAddData}
        vehicles={activeVehicles}
        defaultVehicleId={selectedVehicleId === "all" ? null : selectedVehicleId}
        onPickAction={handleAddDataPick}
      />
      <AddTripDialog
        open={showAdd}
        onOpenChange={(v) => {
          setShowAdd(v);
          if (!v) setEditingTrip(null);
        }}
        vehicles={editingTrip ? vehicles : activeVehicles}
        defaultVehicleId={selectedVehicleId === "all" ? activeVehicles[0]?.id : selectedVehicleId}
        initialTrip={editingTrip}
        unit={unit}
        tz={tz}
        onSubmit={handleAddTrip}
      />
      <BulkImportDialog
        open={showBulkImport}
        onOpenChange={(v) => {
          setShowBulkImport(v);
          if (!v) {
            setBulkImportKind(null);
            setBulkImportVehicleId(null);
          }
        }}
        vehicles={activeVehicles}
        defaultVehicleId={bulkImportVehicleId || (selectedVehicleId === "all" ? activeVehicles[0]?.id : selectedVehicleId)}
        defaultKind={bulkImportKind}
        unit={unit}
        onImported={handleBulkImported}
      />
      <VehicleManageDialog
        open={showManage}
        onOpenChange={setShowManage}
        vehicles={vehicles}
        unit={unit}
        onCreate={handleVehicleCreate}
        onUpdate={handleVehicleUpdate}
        onDelete={handleVehicleDelete}
      />
      <AddEnergyDialog
        open={showEnergy}
        onOpenChange={(v) => {
          setShowEnergy(v);
          if (!v) setEditingEnergy(null);
        }}
        vehicles={editingEnergy ? vehicles : activeVehicles}
        defaultVehicleId={
          energyDefaults.vehicleId ||
          (selectedVehicleId === "all" ? activeVehicles[0]?.id : selectedVehicleId)
        }
        defaultType={energyDefaults.type || null}
        initialEvent={editingEnergy}
        currencySymbol={currencySymbol}
        chargingRates={settings?.charging_rates || null}
        tz={tz}
        onSubmit={handleAddEnergy}
      />
      <SettingsDialog
        open={showSettings}
        onOpenChange={setShowSettings}
        settings={settings}
        onSave={handleSaveSettings}
        unit={unit}
        onUnitChange={setUnit}
      />
      <FeedbackDialog open={showFeedback} onOpenChange={setShowFeedback} />
      <WhatsNewDialog />

      {/* Mobile-only contextual FAB — Dashboard exposes Add Trip + Add Energy */}
      <MobileFab
        testid="dashboard-fab"
        actions={[
          {
            key: "add-trip",
            label: "Add Trip",
            icon: RouteIcon,
            testid: "dashboard-fab-add-trip",
            onClick: () => setShowAdd(true),
          },
          {
            key: "add-energy",
            label: "Add Energy",
            icon: ZapIcon,
            testid: "dashboard-fab-add-energy",
            onClick: () => {
              setEnergyDefaults({ vehicleId: null });
              setShowEnergy(true);
            },
          },
        ]}
      />
      <ConfirmDialog />
    </div>
  );
}
